import React, { useState, useEffect, useCallback } from 'react';
import { batchService, customerService, productService, warehouseService, gatePassService, invoiceService, gdService, purchaseService } from '../../services/financeService';
import AttachmentPanel from './AttachmentPanel';
import SearchableSelect from './SearchableSelect';
import CsvActions from '../common/CsvActions';
import './Financial.css';

const BATCH_CSV_HEADERS = ['Batch Code', 'Product', 'Warehouse', 'Status', 'Created Date', 'Notes'];
const buildBatchCsvRows = (batches) => batches.map(b => [
  b.batchCode || '', b.productName || '', b.godownName || '',
  b.status || '', b.createdAt?.split('T')[0] || '', (b.notes || '').replace(/,/g, ';')
]);

// ── constants ─────────────────────────────────────────────────────────────────

// Grade options — locked to the real, confirmed values from this business's own sourcing
// records (2026-08-19). Was a free-typing datalist; anything outside these 4 goes through the
// explicit "Other" escape hatch in LockedSelect now, instead of silently accepting a typo.
const GRADE_OPTIONS = ['Chaant', 'Jamboo', 'Goli', 'Safi'];

// Wastage reasons — merged in from the old standalone Wastage page (2026-08-18), same list.
const WASTAGE_REASONS = ['DAMAGED', 'EXPIRED', 'CONTAMINATED', 'PROCESSING_LOSS', 'SPILLAGE', 'REJECTED_QC', 'TRANSIT_DAMAGE', 'OTHER'];
const fmtReason = (v) => v ? v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

// Bardana (packaging) options — locked to the real, confirmed values (2026-08-19), same
// "Other" escape hatch as Grade above.
const BARDANA_OPTIONS = ['Jute', 'PP Bag Yellow', 'PP Bag Red'];

// Operation types map: enum value → display label
const OP_TYPES = ['LOADING','PACKAGING_ONLY','SHIFTING','REPACKAGING','MARKETING','MISC','CUSTOM'];
const OP_LABEL = {
  LOADING:       'Loading (per kg)',
  PACKAGING_ONLY:'Packaging Only',
  SHIFTING:      'Shifting / Moving',
  REPACKAGING:   'Repackaging',
  MARKETING:     'Marketing',
  MISC:          'Miscellaneous',
  CUSTOM:        'Custom (see notes)',
};
const STATUSES     = ['OPEN','PROCESSING','EXPORTED','CLOSED'];

const STATUS_STYLE = {
  OPEN:       { background: 'var(--color-success-light)', color: 'var(--color-success)' },
  PROCESSING: { background: 'var(--color-warning-light)', color: 'var(--color-warning)' },
  EXPORTED:   { background: 'var(--color-info-light)', color: 'var(--color-primary)' },
  CLOSED:     { background: 'var(--color-bg-tertiary)', color: 'var(--color-neutral-700)' },
};

const pkr  = (v) => v == null ? '—' : 'PKR ' + Number(v).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const usd  = (v) => v == null ? '—' : 'USD ' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const kg   = (v) => v == null ? '—' : Number(v).toLocaleString() + ' kg';
const fmt  = (s) => s ? s.replace(/_/g, ' ') : '—';

const badge = (text, styleMap) => {
  const s = styleMap[text] || { background: 'var(--color-bg-tertiary)', color: 'var(--color-neutral-700)' };
  return <span style={{ ...s, padding: '2px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600 }}>{text}</span>;
};

const Toast = ({ toast }) => toast ? (
  <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
    background: toast.type === 'error' ? 'var(--color-error)' : 'var(--color-success)',
    color: '#fff', padding: '12px 20px', borderRadius: 10, fontSize: 14, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
    {toast.msg}
  </div>
) : null;

const Spinner = () => <div style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-secondary)' }}>Loading…</div>;

const ConfirmModal = ({ message, onConfirm, onCancel }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onCancel}>
    <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 360, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
      <p style={{ fontSize: 15, color: 'var(--color-neutral-700)', marginBottom: 20 }}>{message}</p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button style={{ padding: '7px 18px', borderRadius: 6, border: '1px solid var(--color-border-strong)', background: 'var(--color-neutral-50)', cursor: 'pointer', fontSize: 13 }} onClick={onCancel}>Cancel</button>
        <button style={{ padding: '7px 18px', borderRadius: 6, border: 'none', background: 'var(--color-error)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }} onClick={onConfirm}>Delete</button>
      </div>
    </div>
  </div>
);

const Input = ({ label, hint, ...props }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    {label && <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 500 }}>{label}</label>}
    <input {...props} style={{ padding: '7px 10px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 13, ...props.style }} />
    {hint && <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>{hint}</span>}
  </div>
);

// Locked dropdown with an explicit "Other" escape hatch — real confirmed values are the only
// way to select silently; anything else has to be deliberately typed, so it can't happen by typo.
const LockedSelect = ({ label, options, value, onChange, placeholder }) => {
  const isKnown = options.includes(value);
  // "Other" mode is sticky once chosen (tracked separately from value) so the free-text box
  // doesn't disappear the instant the user clears it back to empty while typing.
  const [forceOther, setForceOther] = useState(value !== '' && !isKnown);
  const showOther = forceOther || (value !== '' && !isKnown);
  const selectValue = showOther ? '__other__' : value;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 500 }}>{label}</label>}
      <select value={selectValue} onChange={e => {
          if (e.target.value === '__other__') { setForceOther(true); onChange(''); }
          else { setForceOther(false); onChange(e.target.value); }
        }}
        style={{ padding: '7px 10px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 13 }}>
        <option value="">Select...</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
        <option value="__other__">Other (type below)</option>
      </select>
      {showOther && (
        <input value={value} onChange={e => onChange(e.target.value)}
          placeholder={placeholder || 'Type value...'} autoFocus
          style={{ padding: '7px 10px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 13 }} />
      )}
    </div>
  );
};

const Select = ({ label, options, ...props }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    {label && <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 500 }}>{label}</label>}
    <select {...props} style={{ padding: '7px 10px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 13, background: '#fff', ...props.style }}>
      <option value="">— select —</option>
      {options.map(o => typeof o === 'string'
        ? <option key={o} value={o}>{fmt(o)}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const today = () => new Date().toISOString().split('T')[0];

// ── main component ────────────────────────────────────────────────────────────

const BatchManagement = () => {
  const [batches, setBatches]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null);
  const [activeTab, setActiveTab]   = useState('amad');
  const [toast, setToast]           = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [products, setProducts]     = useState([]);
  const [customers, setCustomers]   = useState([]);
  const [vendors, setVendors]       = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadBatches = useCallback(async () => {
    try {
      setLoading(true);
      const res = await batchService.getAll();
      setBatches(res.data || []);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load reference data once on mount
  useEffect(() => {
    loadBatches();
    productService.getAll({ activeOnly: true })
      // Labor/service placeholder products (CONSUMABLE) are cost entries, not physical
      // commodities — a Batch always tracks a real shipment of goods.
      .then(r => setProducts((r.data || r || [])
        .filter(p => p.category !== 'CONSUMABLE')
        .map(p => ({ value: p.id, label: p.name, code: p.productCode }))))
      .catch(() => {});
    customerService.getByType('VENDOR')
      .then(r => setVendors((r.data || r || []).map(c => ({ value: c.id, label: c.name, code: c.customerCode }))))
      .catch(() => {});
    customerService.getAll()
      .then(r => setCustomers((r.data || r || [])
        .filter(c => c.customerType === 'CUSTOMER' || c.customerType === 'BOTH')
        .map(c => ({ value: c.id, label: c.name, code: c.customerCode }))))
      .catch(() => {});
    warehouseService.getAll(true)
      .then(r => setWarehouses((r.data || r || []).map(w => ({ value: w.name, label: w.name + (w.location ? ` — ${w.location}` : '') }))))
      .catch(() => {});
  }, [loadBatches]);

  const openBatch = (batch) => {
    setSelected(batch);
    setActiveTab('amad');
  };

  if (selected) {
    return (
      <BatchDetail
        batch={selected}
        onBack={() => { setSelected(null); loadBatches(); }}
        showToast={showToast}
        toast={toast}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        customers={customers}
        vendors={vendors}
        warehouses={warehouses}
      />
    );
  }

  return (
    <div className="financial-container">
      <Toast toast={toast} />

      <div className="financial-header">
        <div>
          <h2 className="financial-title">Batch Management</h2>
          <p className="financial-subtitle">
            Track mandi purchases, sorting, container costs, and export dispatch per batch
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <CsvActions filename="batches" headers={BATCH_CSV_HEADERS} matrix={buildBatchCsvRows(batches)} />
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New Batch
          </button>
        </div>
      </div>

      {showCreate && (
        <CreateBatchForm
          products={products}
          warehouses={warehouses}
          onCreated={(b) => { setShowCreate(false); setBatches(prev => [b, ...prev]); showToast('Batch created'); }}
          onClose={() => setShowCreate(false)}
        />
      )}

      {loading ? <Spinner /> : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                {['Batch Code','Product','Month','Godown','Status',''].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {batches.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: 'var(--color-text-tertiary)' }}>No batches yet. Create your first batch to start tracking.</td></tr>
              )}
              {batches.map(b => (
                <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => openBatch(b)}>
                  <td style={{ fontWeight: 600 }}>{b.batchCode}</td>
                  <td>{b.productName}</td>
                  <td className="nowrap">{b.batchMonth ? b.batchMonth.substring(0, 7) : '—'}</td>
                  <td style={{ color: 'var(--color-text-secondary)' }}>{b.godownName || '—'}</td>
                  <td>{badge(b.status, STATUS_STYLE)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="action-btn btn-secondary" onClick={(e) => { e.stopPropagation(); openBatch(b); }}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Create Batch Form ─────────────────────────────────────────────────────────

const CreateBatchForm = ({ products, warehouses, onCreated, onClose }) => {
  const initialMonth = today().substring(0, 7);
  const [form, setForm] = useState({
    batchCode: '', productId: '', productName: '', batchMonth: initialMonth + '-01', godownName: '', notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Auto-fill batch code when month changes (or on mount)
  const fetchSuggestedCode = useCallback(async (monthStr) => {
    try {
      const code = await batchService.suggestCode(monthStr);
      if (code) setForm(p => ({ ...p, batchCode: code }));
    } catch {}
  }, []);

  useEffect(() => { fetchSuggestedCode(initialMonth); }, [fetchSuggestedCode, initialMonth]);

  const handleProductChange = (e) => {
    const id = e.target.value;
    set('productId', id);
    if (id) {
      const found = products.find(p => String(p.value) === id);
      if (found) set('productName', found.label);
    }
  };

  const handleMonthChange = (e) => {
    const m = e.target.value;
    set('batchMonth', m + '-01');
    fetchSuggestedCode(m);
  };

  const submit = async () => {
    if (!form.productId) {
      setErr('Please select a product from the catalog — free text is not allowed to prevent duplicate entries.'); return;
    }
    setLoading(true); setErr(null);
    try {
      const res = await batchService.create({
        ...form,
        batchMonth: form.batchMonth.substring(0, 7) + '-01'
      });
      onCreated(res.data);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ background: 'var(--color-neutral-50)', border: '1px solid var(--color-border-medium)', borderRadius: 10, padding: 20, marginBottom: 20 }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Create New Batch</h3>
      {err && <div style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <Input label="Batch Code" placeholder="Auto-generated (e.g. B042026-1)" value={form.batchCode} onChange={e => set('batchCode', e.target.value)} />
        {products.length > 0 ? (
          <SearchableSelect label="Product *" value={form.productId}
            options={products.map(o => typeof o === 'string' ? { value: o, label: o } : o)}
            onChange={(value) => handleProductChange({ target: { value: String(value) } })} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 500 }}>Product *</label>
            <div style={{ padding: '7px 10px', border: '1px solid var(--color-warning-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-warning-light)', color: 'var(--color-warning-strong)' }}>
              No products in catalog. Go to <strong>Product Catalog → + Add Product</strong> first.
            </div>
          </div>
        )}
        <Input label="Month" type="month" value={form.batchMonth.substring(0,7)} onChange={handleMonthChange} />
        {warehouses.length > 0
          ? <Select label="Godown / Warehouse" value={form.godownName} onChange={e => set('godownName', e.target.value)} options={warehouses} />
          : <Input label="Godown / Warehouse" placeholder="e.g. Haji Mumtaz Godown" value={form.godownName} onChange={e => set('godownName', e.target.value)} />
        }
        <Input label="Notes" value={form.notes} onChange={e => set('notes', e.target.value)} />
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button onClick={submit} disabled={loading}
          style={{ background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 20px', cursor: 'pointer', fontWeight: 600 }}>
          {loading ? 'Creating…' : 'Create Batch'}
        </button>
        <button onClick={onClose}
          style={{ background: 'var(--color-bg-tertiary)', color: 'var(--color-neutral-700)', border: 'none', borderRadius: 7, padding: '8px 16px', cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );
};

// ── Batch Detail ──────────────────────────────────────────────────────────────

const BatchDetail = ({ batch, onBack, showToast, toast, activeTab, setActiveTab, customers, vendors, warehouses }) => {
  const [pnl, setPnl]                   = useState(null);
  const [linkedDocs, setLinkedDocs]     = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const loadPnl = useCallback(async () => {
    try { const r = await batchService.getPnl(batch.id); setPnl(r.data); } catch {}
  }, [batch.id]);

  const loadLinkedDocs = useCallback(async () => {
    try { const r = await batchService.getLinkedDocs(batch.id); setLinkedDocs(r.data); } catch {}
  }, [batch.id]);

  useEffect(() => {
    if (activeTab === 'pnl') loadPnl();
    if (activeTab === 'docs') loadLinkedDocs();
  }, [activeTab, loadPnl, loadLinkedDocs]);

  const updateStatus = async (s) => {
    setStatusLoading(true);
    try { await batchService.updateStatus(batch.id, s); showToast('Status updated'); batch.status = s; }
    catch (e) { showToast(e.message, 'error'); }
    finally { setStatusLoading(false); }
  };

  const tabs = [
    { key: 'amad',     label: 'Sourcing' },
    { key: 'grading',  label: 'Grading' },
    { key: 'container',label: 'Processing' },
    { key: 'tarseel',  label: 'Dispatch' },
    { key: 'docs',     label: 'Linked Docs' },
    { key: 'pnl',      label: 'P&L' },
  ];

  return (
    <div className="financial-container">
      {/* Toast was previously only rendered by the list view (BatchManagement's own return) —
          since opening a batch fully replaces that view (`if (selected) return <BatchDetail/>`),
          any showToast() called from in here (status change, grading, processing, etc.) set the
          state but had nothing mounted to display it — a real silent-failure bug, caught
          2026-09-23 when a batch-close error (expired session) produced no visible feedback at
          all. Fixed by rendering the same Toast here too. */}
      <Toast toast={toast} />
      {/* Header */}
      <div className="financial-header" style={{ alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
          <button onClick={onBack} className="btn-secondary" aria-label="Back to batches" style={{ padding: '0 12px' }}>
            ← Back
          </button>
          <div style={{ minWidth: 0 }}>
            <h2 className="financial-title">{batch.batchCode}</h2>
            <p className="financial-subtitle" style={{ marginTop: 2 }}>{batch.productName} · {batch.godownName || '—'} · {batch.batchMonth?.substring(0,7)}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {badge(batch.status, STATUS_STYLE)}
          <select value={batch.status} onChange={e => updateStatus(e.target.value)} disabled={statusLoading}
            className="form-control" style={{ width: 'auto', minHeight: 34, padding: '4px 10px', fontSize: 13 }} aria-label="Batch status">
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-navigation" role="tablist">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} role="tab" aria-selected={activeTab === t.key}
            className={`tab-button ${activeTab === t.key ? 'active' : ''}`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'amad'      && <AmadTab      batchId={batch.id} showToast={showToast} vendors={vendors} warehouses={warehouses} defaultWarehouse={batch.godownName} batchCode={batch.batchCode} />}
      {activeTab === 'grading'   && <GradingTab   batchId={batch.id} showToast={showToast} productName={batch.productName} defaultWarehouse={batch.godownName} />}
      {activeTab === 'container' && <ContainerTab batchId={batch.id} showToast={showToast} customers={customers} />}
      {activeTab === 'tarseel'   && <TarseelTab   batchId={batch.id} showToast={showToast} customers={customers} batchCode={batch.batchCode} />}
      {activeTab === 'docs'      && <LinkedDocsTab docs={linkedDocs} reload={loadLinkedDocs} batchId={batch.id} />}
      {activeTab === 'pnl'       && <PnlTab       pnl={pnl} reload={loadPnl} />}
    </div>
  );
};

// ── Amad Tab ──────────────────────────────────────────────────────────────────

const AmadTab = ({ batchId, showToast, vendors, warehouses = [], defaultWarehouse = '', batchCode = '' }) => {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [entryPasses, setEntryPasses] = useState([]);
  const [importing, setImporting] = useState(false);
  const [form, setForm]           = useState({
    entryDate: today(), vendorId: '', warehouseName: defaultWarehouse, supplierCode: '', supplierName: '',
    boriCount: '', weightKg: '', ratePerKg: '', sourcingLabor: '0', notes: ''
  });
  const [saving, setSaving]       = useState(false);
  const [delConfirmId, setDelConfirmId] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [editLabor, setEditLabor]   = useState(null); // { id, value }
  const [editingEntry, setEditingEntry] = useState(null); // full entry being edited
  const [editForm, setEditForm]     = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [createPoFor, setCreatePoFor] = useState(null); // entry to create PO from
  const [poSaving, setPoSaving]     = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await batchService.getAmad(batchId);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) { showToast(e.message, 'error'); }
    finally { setLoading(false); }
  }, [batchId]);

  useEffect(() => {
    load();
    // Load ENTRY gate passes for import option
    gatePassService.getByType('ENTRY')
      .then(r => {
        const all = r.data || r || [];
        setEntryPasses(all.filter(gp =>
          gp.status === 'RECEIVED' &&
          (!gp.batchCode || gp.batchCode === batchCode)
        ));
      })
      .catch(() => {});
  }, [load, batchCode]);

  const importFromGP = async (gpId) => {
    if (!gpId) return;
    setImporting(true);
    try {
      await batchService.importAmadFromGatePass(batchId, gpId);
      showToast('Imported from gate pass');
      await load();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setImporting(false); }
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleVendorChange = (e) => {
    const id = e.target.value;
    set('vendorId', id);
    if (id) {
      const found = vendors.find(v => String(v.value) === id);
      if (found) set('supplierName', found.label);
    } else { set('supplierName', ''); }
  };

  const previewAmount = form.weightKg && form.ratePerKg
    ? (parseFloat(form.weightKg) * parseFloat(form.ratePerKg)).toFixed(0) : '—';

  const save = async () => {
    if ((!form.vendorId && !form.supplierName) || !form.weightKg || !form.ratePerKg) {
      showToast('Supplier, weight and rate per KG are required', 'error'); return;
    }
    setSaving(true);
    try {
      await batchService.addAmad(batchId, { ...form });
      setForm(p => ({ ...p, vendorId: '', supplierCode: '', supplierName: '', boriCount: '', weightKg: '', ratePerKg: '', sourcingLabor: '0', notes: '' }));
      setShowManual(false);
      await load();
      showToast('Entry added');
    } catch (e) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const del = (id) => setDelConfirmId(id);
  const doDelete = async () => {
    const id = delConfirmId; setDelConfirmId(null);
    try { await batchService.deleteAmad(batchId, id); showToast('Deleted'); load(); }
    catch (e) { showToast(e.message, 'error'); }
  };

  const startEdit = (r) => {
    setEditingEntry(r.id);
    setEditForm({
      entryDate: r.entryDate || today(),
      supplierName: r.supplierName || '',
      warehouseName: r.warehouseName || '',
      boriCount: r.boriCount != null ? String(r.boriCount) : '',
      weightKg: r.weightKg != null ? String(r.weightKg) : '',
      ratePerKg: r.ratePerKg != null ? String(r.ratePerKg) : (r.ratePer40Kg ? String(Number(r.ratePer40Kg) / 40) : ''),
      sourcingLabor: r.sourcingLabor != null ? String(r.sourcingLabor) : '0',
      notes: r.notes || '',
    });
  };
  const setEF = (k, v) => setEditForm(p => ({ ...p, [k]: v }));

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      await batchService.patchAmad(batchId, editingEntry, editForm);
      showToast('Entry updated');
      setEditingEntry(null);
      await load();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setEditSaving(false); }
  };

  const doCreatePo = async () => {
    const r = createPoFor;
    setPoSaving(true);
    try {
      const po = await purchaseService.create({
        vendorId: r.vendor?.id || null,
        purchaseDate: r.entryDate,
        currency: 'PKR',
        notes: `Auto-created from batch sourcing entry (${r.supplierName})`,
        updateStock: false,
        items: [{
          itemName: r.supplierName || 'Raw Material',
          quantity: r.weightKg,
          unit: 'KG',
          unitPrice: r.ratePerKg || (r.ratePer40Kg ? Number(r.ratePer40Kg) / 40 : 0),
        }],
      });
      // Link PO to entry permanently so button stays hidden after refresh
      await batchService.patchAmad(batchId, r.id, { linkedPurchaseId: po.id });
      showToast('Purchase Order created successfully!', 'success');
      setCreatePoFor(null);
      await load();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setPoSaving(false); }
  };

  const totals = rows.reduce((a, r) => ({
    bori:   a.bori   + (Number(r.boriCount) || 0),
    weight: a.weight + (Number(r.weightKg)  || 0),
    amount: a.amount + (Number(r.totalAmount)|| 0),
  }), { bori: 0, weight: 0, amount: 0 });

  // Already-imported gate pass IDs
  const importedGpIds = new Set(rows.filter(r => r.gatePass).map(r => r.gatePass.id));
  const availableToImport = entryPasses.filter(gp => !importedGpIds.has(gp.id));

  return (
    <div>
      {/* Import from Gate Pass — primary action */}
      {availableToImport.length > 0 && (
        <div style={{ background: 'var(--color-warning-light)', border: '1px solid var(--color-warning-border)', borderRadius: 8, padding: '12px 16px', marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-warning-strong)', marginBottom: 8 }}>
            Gate passes waiting to be imported ({availableToImport.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {availableToImport.map(gp => (
              <button key={gp.id} onClick={() => importFromGP(gp.id)} disabled={importing}
                style={{ background: '#fff', border: '1px solid var(--color-warning-border)', borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontSize: 12, color: 'var(--color-warning-strong)', fontWeight: 500 }}>
                {importing ? '...' : `Import ${gp.gatePassNumber}`}
                <span style={{ color: 'var(--color-warning-strong)', marginLeft: 6 }}>{gp.partyName} · {gp.totalWeight ? Number(gp.totalWeight).toLocaleString() + ' kg' : ''}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, marginTop: 0 }}>
        Gate pass entries appear here automatically when an ENTRY gate pass linked to this batch is marked <strong>Received</strong>.
        Use <em>Add Entry</em> below only for purchases without a gate pass.
      </p>

      {loading ? <Spinner /> : (
        <div style={{ overflowX: 'auto', marginBottom: 16 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--color-neutral-50)', borderBottom: '2px solid var(--color-border-medium)' }}>
                {['Source','Date','Godown','Supplier','Container','Bags','Weight KG','Rate/KG','Receiving Labor','Total PKR',''].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: ['Source','Date','Godown','Supplier','Container'].includes(h) ? 'left' : 'right', color: 'var(--color-neutral-700)', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
                  No entries yet. Mark an ENTRY gate pass as Received, or add manually below.
                </td></tr>
              )}
              {rows.map(r => (
                <React.Fragment key={r.id}>
                <tr style={{ borderBottom: editingEntry === r.id ? 'none' : '1px solid var(--color-bg-tertiary)', background: editingEntry === r.id ? 'var(--color-warning-light)' : 'var(--color-bg-primary)' }}>
                  <td style={{ padding: '7px 10px' }}>
                    {r.gatePass
                      ? <span style={{ background: 'var(--color-success-light)', color: 'var(--color-success)', padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{r.gatePass.gatePassNumber}</span>
                      : <span style={{ background: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)', padding: '1px 8px', borderRadius: 999, fontSize: 11 }}>Manual</span>}
                  </td>
                  <td style={{ padding: '7px 10px' }}>{r.entryDate}</td>
                  <td style={{ padding: '7px 10px', color: 'var(--color-text-secondary)' }}>{r.warehouseName || '—'}</td>
                  <td style={{ padding: '7px 10px', fontWeight: 500 }}>
                    {r.vendor ? <span style={{ color: 'var(--color-primary)' }}>{r.supplierName}</span> : r.supplierName}
                  </td>
                  <td style={{ padding: '7px 10px', color: r.containerNumber ? 'var(--color-accent)' : 'var(--color-text-tertiary)' }}>
                    {/* Goods can arrive via container too (imports), not just leave via one — surfaces
                        the ENTRY gate pass's own container number, added 2026-09-14. */}
                    {r.containerNumber || '—'}
                  </td>
                  <td style={{ padding: '7px 10px', textAlign: 'right' }}>{r.boriCount ? Number(r.boriCount).toLocaleString() : '—'}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'right' }}>{Number(r.weightKg).toLocaleString()}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                    {r.ratePerKg ? Number(r.ratePerKg).toFixed(3) : r.ratePer40Kg ? (Number(r.ratePer40Kg)/40).toFixed(3) : '—'}
                  </td>
                  <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                    {/* Gate-pass-derived rows: Labor already lives on the Gate Pass itself — no
                        second editing surface for the same number here, just show it read-only.
                        Manual (no-gate-pass) rows still need their own inline editor since there's
                        no Gate Pass to set it from. */}
                    {r.gatePass ? (
                      <span style={{ color: Number(r.sourcingLabor) > 0 ? 'var(--color-neutral-700)' : 'var(--color-text-tertiary)' }} title="Set on the Gate Pass — edit it there">
                        {Number(r.sourcingLabor || 0).toLocaleString()}
                      </span>
                    ) : editLabor?.id === r.id ? (
                      <span style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                        <input type="number" value={editLabor.value} autoFocus
                          onChange={e => setEditLabor(p => ({ ...p, value: e.target.value }))}
                          style={{ width: 72, padding: '2px 6px', border: '1px solid var(--color-border-strong)', borderRadius: 4, fontSize: 12, textAlign: 'right' }} />
                        <button onClick={async () => {
                          try {
                            await batchService.patchAmad(batchId, r.id, { sourcingLabor: editLabor.value });
                            showToast('Labor updated'); setEditLabor(null); await load();
                          } catch (e) { showToast(e.message, 'error'); }
                        }} style={{ background: 'var(--color-success)', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>✓</button>
                        <button onClick={() => setEditLabor(null)}
                          style={{ background: 'var(--color-border-medium)', border: 'none', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontSize: 11 }}>✕</button>
                      </span>
                    ) : (
                      <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                        <span style={{ color: Number(r.sourcingLabor) > 0 ? 'var(--color-neutral-700)' : 'var(--color-text-tertiary)' }}>
                          {Number(r.sourcingLabor || 0).toLocaleString()}
                        </span>
                        <button onClick={() => setEditLabor({ id: r.id, value: String(r.sourcingLabor || '0') })}
                          style={{ background: 'var(--color-info-light)', color: 'var(--color-primary)', border: 'none', borderRadius: 4, padding: '1px 7px', cursor: 'pointer', fontSize: 10, fontWeight: 500 }}>
                          + Labor
                        </button>
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--color-success)' }}>{Number(r.totalAmount || 0).toLocaleString()}</td>
                  <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                    <span style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {!r.gatePass && editingEntry !== r.id && (
                        <>
                          <button onClick={() => startEdit(r)}
                            style={{ background: 'var(--color-warning-light)', color: 'var(--color-warning-strong)', border: '1px solid var(--color-warning-border)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 500 }}>
                            Edit
                          </button>
                          {r.linkedPurchaseId ? (
                            <span style={{ fontSize: 10, color: 'var(--color-success)', fontWeight: 600 }}>✓ PO Created</span>
                          ) : (
                            <button onClick={() => setCreatePoFor(r)}
                              style={{ background: 'var(--color-accent-light)', color: 'var(--color-accent-hover)', border: '1px solid var(--color-accent-light)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 500 }}>
                              → PO
                            </button>
                          )}
                        </>
                      )}
                      {editingEntry === r.id && (
                        <button onClick={() => setEditingEntry(null)}
                          style={{ background: 'var(--color-border-medium)', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>Cancel</button>
                      )}
                      <button onClick={() => del(r.id)} style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>✕</button>
                    </span>
                  </td>
                </tr>
                {editingEntry === r.id && (
                  <tr style={{ background: 'var(--color-warning-light)', borderBottom: '2px solid var(--color-warning-border)' }}>
                    <td colSpan={11} style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
                        <Input label="Date" type="date" value={editForm.entryDate} onChange={e => setEF('entryDate', e.target.value)} />
                        <Input label="Supplier" value={editForm.supplierName} onChange={e => setEF('supplierName', e.target.value)} />
                        {warehouses.length > 0
                          ? <Select label="Godown" value={editForm.warehouseName} onChange={e => setEF('warehouseName', e.target.value)} options={warehouses} />
                          : <Input label="Godown" value={editForm.warehouseName} onChange={e => setEF('warehouseName', e.target.value)} />
                        }
                        <Input label="Bags" type="number" value={editForm.boriCount} onChange={e => setEF('boriCount', e.target.value)} />
                        <Input label="Weight KG" type="number" value={editForm.weightKg} onChange={e => setEF('weightKg', e.target.value)} />
                        <Input label="Rate/KG" type="number" value={editForm.ratePerKg} onChange={e => setEF('ratePerKg', e.target.value)} />
                        <Input label="Receiving Labor" type="number" value={editForm.sourcingLabor} onChange={e => setEF('sourcingLabor', e.target.value)} />
                        <Input label="Notes" value={editForm.notes} onChange={e => setEF('notes', e.target.value)} />
                      </div>
                      <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                        <button onClick={saveEdit} disabled={editSaving}
                          style={{ background: 'var(--color-info)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                          {editSaving ? 'Saving…' : 'Save Changes'}
                        </button>
                        <button onClick={() => setEditingEntry(null)}
                          style={{ background: 'var(--color-border-medium)', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
              {rows.length > 0 && (
                <tr style={{ background: 'var(--color-neutral-50)', fontWeight: 700, borderTop: '2px solid var(--color-border-medium)' }}>
                  <td colSpan={5} style={{ padding: '8px 10px' }}>TOTAL ({rows.length} entries)</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totals.bori.toLocaleString()}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totals.weight.toLocaleString()} kg</td>
                  <td colSpan={2}></td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--color-success)' }}>PKR {totals.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Manual add (only for deliveries without a gate pass) */}
      <div style={{ marginTop: 8 }}>
        <button onClick={() => setShowManual(v => !v)}
          style={{ background: showManual ? 'var(--color-info-light)' : 'var(--color-info-light)', color: 'var(--color-info)', border: '1px solid var(--color-info)', borderRadius: 7, padding: '6px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
          {showManual ? '✕ Cancel' : '+ Add Entry Without Gate Pass'}
        </button>
        <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--color-text-tertiary)' }}>Use only for purchases not covered by a gate pass</span>
      </div>

      {showManual && (
        <div style={{ background: 'var(--color-info-light)', border: '1px solid var(--color-info)', borderRadius: 10, padding: 16, marginTop: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
            <Input label="Date" type="date" value={form.entryDate} onChange={e => set('entryDate', e.target.value)} />
            {warehouses.length > 0
              ? <Select label="Godown" value={form.warehouseName} onChange={e => set('warehouseName', e.target.value)} options={warehouses} />
              : <Input label="Godown" value={form.warehouseName} onChange={e => set('warehouseName', e.target.value)} placeholder="Haji Mumtaz…" />
            }
            {vendors.length > 0
              ? <SearchableSelect label="Supplier *" value={form.vendorId}
                  options={vendors.map(o => typeof o === 'string' ? { value: o, label: o } : o)}
                  onChange={(value) => handleVendorChange({ target: { value: String(value) } })} />
              : <Input label="Supplier Name *" value={form.supplierName} onChange={e => set('supplierName', e.target.value)} placeholder="Supplier / vendor name" />
            }
            <Input label="Bags / Units" type="number" value={form.boriCount} onChange={e => set('boriCount', e.target.value)} placeholder="no. of bags or units" />
            <Input label="Weight KG *" type="number" value={form.weightKg} onChange={e => set('weightKg', e.target.value)} placeholder="total kg" />
            <Input label="Rate per KG *" type="number" value={form.ratePerKg} onChange={e => set('ratePerKg', e.target.value)} placeholder="e.g. 2.125" />
            <Input label="Receiving Labor" type="number" value={form.sourcingLabor} onChange={e => set('sourcingLabor', e.target.value)} placeholder="0" />
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>Est. Amount</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-success)' }}>PKR {Number(previewAmount).toLocaleString()}</span>
            </div>
          </div>
          <button onClick={save} disabled={saving}
            style={{ marginTop: 12, background: 'var(--color-info)', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            {saving ? 'Saving…' : 'Add Entry'}
          </button>
        </div>
      )}
      {delConfirmId && <ConfirmModal message="Delete this sourcing entry?" onConfirm={doDelete} onCancel={() => setDelConfirmId(null)} />}

      {createPoFor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 420, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12, color: 'var(--color-text-primary)' }}>Create Purchase Order</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
              A PO will be created for this sourcing entry:
              <div style={{ background: 'var(--color-neutral-50)', borderRadius: 8, padding: '10px 14px', marginTop: 10, fontSize: 12 }}>
                <div><strong>Supplier:</strong> {createPoFor.supplierName || '—'}</div>
                <div><strong>Date:</strong> {createPoFor.entryDate}</div>
                <div><strong>Weight:</strong> {Number(createPoFor.weightKg).toLocaleString()} KG</div>
                <div><strong>Rate/KG:</strong> {createPoFor.ratePerKg ? Number(createPoFor.ratePerKg).toFixed(3) : (createPoFor.ratePer40Kg ? (Number(createPoFor.ratePer40Kg)/40).toFixed(3) : '—')}</div>
                <div><strong>Amount:</strong> PKR {Number(createPoFor.totalAmount || 0).toLocaleString()}</div>
                {!createPoFor.vendor && <div style={{ color: 'var(--color-warning)', marginTop: 6, fontSize: 11 }}>⚠ No vendor linked — PO will be created without a vendor ID</div>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setCreatePoFor(null)} disabled={poSaving}
                style={{ background: 'var(--color-border-medium)', border: 'none', borderRadius: 7, padding: '8px 18px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={doCreatePo} disabled={poSaving || !createPoFor?.vendor}
                title={!createPoFor?.vendor ? 'No vendor linked to this entry — cannot create PO' : ''}
                style={{ background: createPoFor?.vendor ? 'var(--color-accent-hover)' : 'var(--color-text-tertiary)', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 20px', cursor: createPoFor?.vendor ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: 13 }}>
                {poSaving ? 'Creating…' : 'Create PO'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Grading Tab ───────────────────────────────────────────────────────────────

const GradingTab = ({ batchId, showToast, productName, defaultWarehouse }) => {
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(true);
  const emptyForm = { entryType: 'GRADED_RECOVERY', entryDate: today(), grade: '', bagCount: '', weightKg: '', recoveryRatePerBag: '', wastageReason: '', estimatedLossAmount: '', notes: '' };
  const [form, setForm]   = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [delConfirmId, setDelConfirmId] = useState(null);

  const load = useCallback(async () => {
    try { setLoading(true); const r = await batchService.getGrading(batchId); setRows(r.data || []); }
    catch {} finally { setLoading(false); }
  }, [batchId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const isWastage = form.entryType === 'WASTAGE_LOSS';
  const previewRecovery = form.bagCount && form.recoveryRatePerBag
    ? (parseFloat(form.bagCount) * parseFloat(form.recoveryRatePerBag)).toFixed(0) : '—';

  const save = async () => {
    if (isWastage) {
      if (!form.wastageReason || !form.weightKg) { showToast('Reason and weight are required', 'error'); return; }
    } else if (!form.grade || !form.bagCount) {
      showToast('Grade and bag count required', 'error'); return;
    }
    setSaving(true);
    try {
      await batchService.addGrading(batchId, form);
      showToast(isWastage ? 'Wastage recorded — stock updated' : 'Deduction added');
      setForm(p => ({ ...emptyForm, entryType: p.entryType }));
      load();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const del = (id) => setDelConfirmId(id);
  const doDelete = async () => {
    const id = delConfirmId; setDelConfirmId(null);
    try { await batchService.deleteGrading(batchId, id); load(); showToast('Deleted'); }
    catch (e) { showToast(e.message, 'error'); }
  };

  const totals = rows.reduce((a, r) => ({
    bags: a.bags+(r.bagCount||0),
    kg: a.kg+parseFloat(r.weightKg||0),
    recovery: a.recovery+parseFloat(r.recoveryAmount||0),
    loss: a.loss+parseFloat(r.estimatedLossAmount||0),
  }), { bags:0, kg:0, recovery:0, loss:0 });

  return (
    <div>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, marginTop: 0 }}>
        Everything that leaves this batch other than a dispatch — sorted-out stock that gets resold,
        and material that's simply lost — lives here in one place, so the batch's own numbers always
        account for it.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button onClick={() => set('entryType', 'GRADED_RECOVERY')}
          style={{ flex: 1, padding: '10px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13,
            border: !isWastage ? '2px solid var(--color-warning)' : '1px solid var(--color-border-strong)',
            background: !isWastage ? 'var(--color-warning-light)' : '#fff', color: !isWastage ? 'var(--color-warning)' : 'var(--color-text-secondary)' }}>
          Graded Recovery <span style={{ fontWeight: 400 }}>— sorted out, resold later</span>
        </button>
        <button onClick={() => set('entryType', 'WASTAGE_LOSS')}
          style={{ flex: 1, padding: '10px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13,
            border: isWastage ? '2px solid var(--color-error)' : '1px solid var(--color-border-strong)',
            background: isWastage ? 'var(--color-error-light)' : '#fff', color: isWastage ? 'var(--color-error)' : 'var(--color-text-secondary)' }}>
          Wastage / Loss <span style={{ fontWeight: 400 }}>— gone for good</span>
        </button>
      </div>

      {!isWastage ? (
        <div style={{ background: 'var(--color-warning-light)', border: '1px solid var(--color-warning-border)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--color-warning)' }}>+ Add Sorting Deduction</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
            <Input label="Date" type="date" value={form.entryDate} onChange={e => set('entryDate', e.target.value)} />
            <LockedSelect label="Grade / Quality *" options={GRADE_OPTIONS} value={form.grade}
              onChange={v => set('grade', v)} placeholder="Type grade..." />
            <Input label="Bags / Units" type="number" value={form.bagCount} onChange={e => set('bagCount', e.target.value)} />
            <Input label="Weight KG" type="number" value={form.weightKg} onChange={e => set('weightKg', e.target.value)}
              hint="For batch reconciliation only — not used in the recovery math below" />
            {/* Renamed from "Recovery Rate / Unit" — "Unit" read as ambiguous sitting right next to
                Weight KG, as if the rate might be per-kg. It's always per BAG (bagCount × rate),
                confirmed live 2026-09-23; the field itself was already correct, only the label and
                the preview's math were unclear. */}
            <Input label="Recovery Rate / Bag (PKR)" type="number" value={form.recoveryRatePerBag} onChange={e => set('recoveryRatePerBag', e.target.value)} />
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>Est. Recovery</span>
              <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-warning)' }}>
                PKR {previewRecovery === '—' ? '—' : Number(previewRecovery).toLocaleString()}
              </span>
              {previewRecovery !== '—' && (
                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                  {form.bagCount} bags × PKR {form.recoveryRatePerBag}/bag
                </span>
              )}
            </div>
          </div>
          <button onClick={save} disabled={saving}
            style={{ marginTop: 12, background: 'var(--color-warning)', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            {saving ? 'Saving…' : 'Add Deduction'}
          </button>
        </div>
      ) : (
        <div style={{ background: 'var(--color-error-light)', border: '1px solid var(--color-error-border)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
          <h4 style={{ margin: '0 0 4px', fontSize: 14, color: 'var(--color-error)' }}>+ Record Wastage / Loss</h4>
          <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--color-error-strong)' }}>
            {productName ? `Stock will be deducted automatically from ${productName}` : 'Stock will be deducted automatically'}
            {defaultWarehouse ? ` at ${defaultWarehouse}.` : '.'}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
            <Input label="Date" type="date" value={form.entryDate} onChange={e => set('entryDate', e.target.value)} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontWeight: 500 }}>Reason *</label>
              <select value={form.wastageReason} onChange={e => set('wastageReason', e.target.value)}
                style={{ padding: '7px 10px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 13 }}>
                <option value="">Select reason...</option>
                {WASTAGE_REASONS.map(r => <option key={r} value={r}>{fmtReason(r)}</option>)}
              </select>
            </div>
            <Input label="Bags / Units" type="number" value={form.bagCount} onChange={e => set('bagCount', e.target.value)} />
            <Input label="Weight KG *" type="number" value={form.weightKg} onChange={e => set('weightKg', e.target.value)} />
            <Input label="Estimated Loss (PKR)" type="number" value={form.estimatedLossAmount} onChange={e => set('estimatedLossAmount', e.target.value)} />
          </div>
          <Input label="Notes" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional details..." />
          <button onClick={save} disabled={saving}
            style={{ marginTop: 12, background: 'var(--color-error)', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            {saving ? 'Saving…' : 'Record Wastage'}
          </button>
        </div>
      )}

      {loading ? <Spinner /> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--color-neutral-50)', borderBottom: '2px solid var(--color-border-medium)' }}>
                {['Date','Type','Grade / Reason','Bags','Weight KG','Rate/Bag','Recovery / Loss (PKR)',''].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: ['Date','Type','Grade / Reason'].includes(h)?'left':'right', color: 'var(--color-neutral-700)', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const loss = r.entryType === 'WASTAGE_LOSS';
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--color-bg-tertiary)' }}>
                    <td style={{ padding: '7px 10px' }}>{r.entryDate}</td>
                    <td style={{ padding: '7px 10px' }}>
                      <span style={{ fontSize: 11 }}>{loss ? 'Loss' : 'Recovery'}</span>
                    </td>
                    <td style={{ padding: '7px 10px' }}>
                      <span style={{ background: loss ? 'var(--color-error-light)' : 'var(--color-warning-light)', color: loss ? 'var(--color-error)' : 'var(--color-warning-strong)', padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600 }}>{r.grade}</span>
                    </td>
                    <td style={{ padding: '7px 10px', textAlign: 'right' }}>{r.bagCount}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right' }}>{r.weightKg != null ? Number(r.weightKg).toLocaleString() : '—'}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right' }}>{r.recoveryRatePerBag != null ? Number(r.recoveryRatePerBag).toFixed(2) : '—'}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: loss ? 'var(--color-error)' : 'var(--color-warning)' }}>
                      {loss
                        ? (r.estimatedLossAmount != null ? `-${Number(r.estimatedLossAmount).toLocaleString()}` : '—')
                        : (r.recoveryAmount != null ? Number(r.recoveryAmount).toLocaleString() : '—')}
                    </td>
                    <td><button onClick={() => del(r.id)} style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}>✕</button></td>
                  </tr>
                );
              })}
              {rows.length > 0 && (
                <tr style={{ background: 'var(--color-neutral-50)', fontWeight: 700, borderTop: '2px solid var(--color-border-medium)' }}>
                  <td colSpan={3} style={{ padding: '8px 10px' }}>TOTAL</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totals.bags.toLocaleString()}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totals.kg.toLocaleString()}</td>
                  <td></td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                    <div style={{ color: 'var(--color-warning)' }}>+{totals.recovery.toLocaleString(undefined,{maximumFractionDigits:0})}</div>
                    <div style={{ color: 'var(--color-error)' }}>-{totals.loss.toLocaleString(undefined,{maximumFractionDigits:0})}</div>
                  </td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
          {rows.length === 0 && <p style={{ textAlign: 'center', color: 'var(--color-text-tertiary)', padding: 20 }}>No deductions yet.</p>}
        </div>
      )}
      {delConfirmId && <ConfirmModal message="Delete this grading entry?" onConfirm={doDelete} onCancel={() => setDelConfirmId(null)} />}
    </div>
  );
};

// ── Container Cost Tab ────────────────────────────────────────────────────────

const ContainerTab = ({ batchId, showToast, customers }) => {
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]   = useState({
    entryDate: today(), operationType: 'LOADING', containerNumber: '', customerId: '',
    buyerCode: '', buyerName: '', bagCount: '', bardanaType: '',
    bardanaRatePerBag: '', weightKg: '', laborRatePerKg: '', flatCost: '0', notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [delConfirmId, setDelConfirmId] = useState(null);

  const load = useCallback(async () => {
    try { setLoading(true); const r = await batchService.getContainerCosts(batchId); setRows(r.data || []); }
    catch {} finally { setLoading(false); }
  }, [batchId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleCustomerChange = (e) => {
    const id = e.target.value;
    set('customerId', id);
    if (id) {
      const found = customers.find(c => String(c.value) === id);
      if (found) set('buyerName', found.label);
    } else {
      set('buyerName', '');
    }
  };

  const isLoading       = form.operationType === 'LOADING';
  const isPackagingOnly = form.operationType === 'PACKAGING_ONLY';
  const showBagsSection = isLoading || isPackagingOnly;
  const previewLabor   = form.weightKg && form.laborRatePerKg && form.operationType === 'LOADING'
    ? (parseFloat(form.weightKg) * parseFloat(form.laborRatePerKg)).toFixed(0) : form.flatCost || '0';
  const previewBardana = form.bagCount && form.bardanaRatePerBag
    ? (parseFloat(form.bagCount) * parseFloat(form.bardanaRatePerBag)).toFixed(0) : '0';

  const save = async () => {
    setSaving(true);
    try {
      await batchService.addContainerCost(batchId, form);
      showToast('Added');
      setForm(p => ({ ...p, containerNumber: '', customerId: '', buyerCode: '', buyerName: '', bagCount: '', weightKg: '', notes: '' }));
      load();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const del = (id) => setDelConfirmId(id);
  const doDelete = async () => {
    const id = delConfirmId; setDelConfirmId(null);
    try { await batchService.deleteContainerCost(batchId, id); load(); showToast('Deleted'); }
    catch (e) { showToast(e.message, 'error'); }
  };

  const totals = rows.reduce((a,r) => ({ labor: a.labor+parseFloat(r.totalLaborCost||0), bardana: a.bardana+parseFloat(r.totalBardanaCost||0) }), { labor:0, bardana:0 });

  return (
    <div>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, marginTop: 0 }}>
        Enter processing, labor and packaging costs per container. Use non-Loading ops for flat-rate charges (shifting, repackaging, misc).
      </p>
      <div style={{ background: 'var(--color-success-light)', border: '1px solid var(--color-success-border)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <h4 style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--color-success)' }}>+ Add Processing Cost</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
          <Input label="Date" type="date" value={form.entryDate} onChange={e => set('entryDate', e.target.value)} />
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <label style={{ fontSize:12, color:'var(--color-text-secondary)', fontWeight:500 }}>Operation Type</label>
            <select value={form.operationType} onChange={e => set('operationType', e.target.value)}
              style={{ padding:'7px 10px', border:'1px solid var(--color-border-strong)', borderRadius:6, fontSize:13, background:'#fff' }}>
              {OP_TYPES.map(t => <option key={t} value={t}>{OP_LABEL[t] || t}</option>)}
            </select>
          </div>
          <Input label="Container No" value={form.containerNumber} onChange={e => set('containerNumber', e.target.value)} placeholder="MSCU1234567" />
          {customers.length > 0
            ? <SearchableSelect label="Buyer" value={form.customerId}
                options={customers.map(o => typeof o === 'string' ? { value: o, label: o } : o)}
                onChange={(value) => handleCustomerChange({ target: { value: String(value) } })} />
            : <Input label="Buyer Name" value={form.buyerName} onChange={e => set('buyerName', e.target.value)} placeholder="Buyer name" />
          }
          {/* Packaging fields — shown for LOADING and PACKAGING_ONLY */}
          {showBagsSection && <>
            <Input label="Bags / Units" type="number" value={form.bagCount} onChange={e => set('bagCount', e.target.value)} />
            <LockedSelect label="Packaging Type" options={BARDANA_OPTIONS} value={form.bardanaType}
              onChange={v => set('bardanaType', v)} placeholder="Type packaging type..." />
            <Input label="Packaging Rate / Bag" type="number" value={form.bardanaRatePerBag} onChange={e => set('bardanaRatePerBag', e.target.value)} placeholder="cost per bag" />
          </>}
          {/* Labor fields — only for LOADING */}
          {isLoading && <>
            <Input label="Weight KG" type="number" value={form.weightKg} onChange={e => set('weightKg', e.target.value)} />
            <Input label="Processing Labor Rate / KG" type="number" value={form.laborRatePerKg} onChange={e => set('laborRatePerKg', e.target.value)} />
          </>}
          {/* Flat cost — for all other op types */}
          {!isLoading && !isPackagingOnly && <Input label="Flat Cost (PKR)" type="number" value={form.flatCost} onChange={e => set('flatCost', e.target.value)} />}
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <span style={{ fontSize:11, color:'var(--color-text-secondary)' }}>Preview</span>
            <span style={{ fontSize:12, color:'var(--color-success)' }}>Processing Labor: PKR {Number(previewLabor).toLocaleString()}</span>
            <span style={{ fontSize:12, color:'var(--color-success)' }}>Packaging: PKR {Number(previewBardana).toLocaleString()}</span>
          </div>
        </div>
        <button onClick={save} disabled={saving}
          style={{ marginTop: 12, background: 'var(--color-success)', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
          {saving ? 'Saving…' : 'Add'}
        </button>
      </div>
      {loading ? <Spinner /> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--color-neutral-50)', borderBottom: '2px solid var(--color-border-medium)' }}>
                {['Date','Type','Container','Buyer','Bags','Packaging','Wt KG','Processing Labor PKR','Packaging PKR','Total PKR',''].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: ['Date','Type','Container','Buyer'].includes(h)?'left':'right', color: 'var(--color-neutral-700)', fontWeight: 600, whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--color-bg-tertiary)', background: !r.containerNumber ? 'var(--color-neutral-50)' : '' }}>
                  <td style={{ padding: '7px 10px' }}>{r.entryDate}</td>
                  <td style={{ padding: '7px 10px' }}><span style={{ fontSize:11, background:'var(--color-info-light)', color:'var(--color-info)', padding:'1px 6px', borderRadius:9999 }}>{OP_LABEL[r.operationType] || fmt(r.operationType)}</span></td>
                  <td style={{ padding: '7px 10px', fontFamily: 'var(--font-mono)' }}>{r.containerNumber || <span style={{color:'var(--color-text-tertiary)'}}>internal</span>}</td>
                  <td style={{ padding: '7px 10px' }}>
                    {r.customer ? <span style={{ color: 'var(--color-primary)' }}>{r.buyerName}</span> : (r.buyerName || '—')}
                  </td>
                  <td style={{ padding: '7px 10px', textAlign:'right' }}>{r.bagCount?.toLocaleString() || '—'}</td>
                  <td style={{ padding: '7px 10px', textAlign:'right', color:'var(--color-text-secondary)' }}>{r.bardanaType || '—'}</td>
                  <td style={{ padding: '7px 10px', textAlign:'right' }}>{Number(r.weightKg||0).toLocaleString()}</td>
                  <td style={{ padding: '7px 10px', textAlign:'right' }}>{Number(r.totalLaborCost||0).toLocaleString()}</td>
                  <td style={{ padding: '7px 10px', textAlign:'right' }}>{Number(r.totalBardanaCost||0).toLocaleString()}</td>
                  <td style={{ padding: '7px 10px', textAlign:'right', fontWeight:600, color:'var(--color-success)' }}>{(Number(r.totalLaborCost||0)+Number(r.totalBardanaCost||0)).toLocaleString()}</td>
                  <td><button onClick={() => del(r.id)} style={{ background:'var(--color-error-light)', color:'var(--color-error)', border:'none', borderRadius:4, padding:'2px 8px', cursor:'pointer', fontSize:11 }}>✕</button></td>
                </tr>
              ))}
              {rows.length > 0 && (
                <tr style={{ background:'var(--color-neutral-50)', fontWeight:700, borderTop:'2px solid var(--color-border-medium)' }}>
                  <td colSpan={7} style={{ padding:'8px 10px' }}>TOTAL</td>
                  <td style={{ padding:'8px 10px', textAlign:'right', color:'var(--color-success)' }}>PKR {totals.labor.toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                  <td style={{ padding:'8px 10px', textAlign:'right', color:'var(--color-success)' }}>PKR {totals.bardana.toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                  <td style={{ padding:'8px 10px', textAlign:'right', color:'var(--color-success)' }}>PKR {(totals.labor+totals.bardana).toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
          {rows.length === 0 && <p style={{ textAlign:'center', color:'var(--color-text-tertiary)', padding:20 }}>No container costs yet.</p>}
        </div>
      )}
      {delConfirmId && <ConfirmModal message="Delete this processing entry?" onConfirm={doDelete} onCancel={() => setDelConfirmId(null)} />}
    </div>
  );
};

// ── Tarseel Tab ───────────────────────────────────────────────────────────────

const TarseelTab = ({ batchId, showToast, customers, batchCode }) => {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [gatePasses, setGatePasses] = useState([]);
  const [invoices, setInvoices]     = useState([]);
  const [gds, setGds]               = useState([]);
  const [batchSummary, setBatchSummary] = useState(null);
  const [weightWarn, setWeightWarn]     = useState('');
  const [form, setForm] = useState({
    dispatchDate: today(), containerNumber: '', customerId: '', buyerCode: '', buyerName: '',
    weightKg: '', bagCount: '', bardanaType: '', destinationPort: '', destinationCountry: '',
    gatePassId: '', invoiceId: '', gdId: '', invoiceNumber: '', notes: ''
  });
  const [saving, setSaving]             = useState(false);
  const [delConfirmId, setDelConfirmId] = useState(null);
  const [noInvoiceConfirm, setNoInvoiceConfirm] = useState(false);

  const load = useCallback(async () => {
    try { setLoading(true); const r = await batchService.getTarseel(batchId); setRows(r.data || []); }
    catch {} finally { setLoading(false); }
  }, [batchId]);

  // Load linkable documents — gate passes (EXIT), invoices (EXPORT), GDs + batch weight summary
  useEffect(() => {
    load();
    batchService.getPnl(batchId)
      .then(r => setBatchSummary(r.data || r))
      .catch(() => {});
    gatePassService.getByType('EXIT')
      .then(r => setGatePasses((r.data || r || [])
        .filter(gp => !gp.batchCode || gp.batchCode === batchCode)
        .map(gp => ({
          value: gp.id,
          label: `${gp.gatePassNumber} – ${gp.partyName}${gp.containerNumber ? ' · '+gp.containerNumber : ''}${gp.batchCode ? ' ✓' : ''}`,
          containerNumber: gp.containerNumber || '',
          totalWeight: gp.totalWeight || '',
          totalQuantity: gp.totalQuantity || '',
          partyName: gp.partyName || '',
          gatePassDate: gp.gatePassDate || '',
          gatePassNumber: gp.gatePassNumber || '',
        }))))
      .catch(() => {});
    invoiceService.getAll()
      .then(r => setInvoices((r.data || r || [])
        .filter(inv => inv.status !== 'CANCELLED' && (!inv.batchCode || inv.batchCode === batchCode))
        .map(inv => ({
          value: inv.id,
          salesType: inv.salesType || 'LOCAL',
          label: `[${inv.salesType || 'LOCAL'}] ${inv.invoiceNumber} – ${inv.customer?.name || inv.customerName || ''}${inv.batchCode ? ' ✓' : ''}`
        }))))
      .catch(() => {});
    gdService.getAll()
      .then(r => setGds((r.data || r || [])
        .filter(gd => !gd.batchCode || gd.batchCode === batchCode)
        .map(gd => ({
          value: gd.id,
          label: `${gd.gdNumber} – ${gd.consigneeName}${gd.batchCode ? ' ✓' : ''}`
        }))))
      .catch(() => {});
  }, [load]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleCustomerChange = (e) => {
    const id = e.target.value;
    set('customerId', id);
    if (id) {
      const found = customers.find(c => String(c.value) === id);
      if (found) set('buyerName', found.label);
    } else {
      set('buyerName', '');
    }
  };

  const handleInvoiceChange = (e) => {
    const id = e.target.value;
    set('invoiceId', id);
    if (id) {
      const found = invoices.find(i => String(i.value) === id);
      if (found) {
        const num = found.label.split(' – ')[0];
        set('invoiceNumber', num);
      }
    }
  };

  const handleGatePassChange = (e) => {
    const id = e.target.value;
    set('gatePassId', id);
    setWeightWarn('');
    if (id) {
      const gp = gatePasses.find(g => String(g.value) === id);
      if (gp) {
        if (gp.containerNumber) set('containerNumber', gp.containerNumber);
        if (gp.totalWeight && parseFloat(gp.totalWeight) > 0) {
          set('weightKg', String(gp.totalWeight));
        }
        if (gp.totalQuantity && parseFloat(gp.totalQuantity) > 0) set('bagCount', String(Math.round(parseFloat(gp.totalQuantity))));
        if (gp.gatePassDate) set('dispatchDate', gp.gatePassDate);
      }
    }
  };

  const checkWeightMismatch = (dispatchKg) => {
    if (!form.gatePassId) return;
    const gp = gatePasses.find(g => String(g.value) === form.gatePassId);
    if (gp && gp.totalWeight && parseFloat(gp.totalWeight) > 0) {
      const diff = Math.abs(parseFloat(dispatchKg) - parseFloat(gp.totalWeight));
      if (diff > 1) {
        setWeightWarn(`Gate pass weight is ${Number(gp.totalWeight).toLocaleString()} kg — dispatch weight differs by ${diff.toLocaleString()} kg`);
      } else {
        setWeightWarn('');
      }
    }
  };

  const fillFromGatePass = (gp) => {
    setForm(p => ({
      ...p,
      gatePassId: String(gp.value),
      containerNumber: gp.containerNumber || p.containerNumber,
      weightKg: gp.totalWeight && parseFloat(gp.totalWeight) > 0 ? String(gp.totalWeight) : p.weightKg,
      bagCount: gp.totalQuantity && parseFloat(gp.totalQuantity) > 0 ? String(Math.round(parseFloat(gp.totalQuantity))) : p.bagCount,
      dispatchDate: gp.gatePassDate || p.dispatchDate,
    }));
  };

  const doSave = async () => {
    setNoInvoiceConfirm(false);
    setSaving(true);
    try {
      await batchService.addTarseel(batchId, form);
      showToast('Container dispatched');
      setForm(p => ({ ...p, containerNumber:'', customerId:'', buyerCode:'', buyerName:'', weightKg:'', bagCount:'', gatePassId:'', invoiceId:'', gdId:'', invoiceNumber:'', notes:'' }));
      setWeightWarn('');
      load();
      batchService.getPnl(batchId).then(r => setBatchSummary(r.data || r)).catch(() => {});
    } catch (e) { showToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const save = async () => {
    if (!form.containerNumber || (!form.customerId && !form.buyerName) || !form.weightKg) {
      showToast('Container, buyer and weight are required', 'error'); return;
    }
    if (!form.gatePassId) {
      const linkedInv = invoices.find(i => String(i.value) === String(form.invoiceId));
      if (!linkedInv || linkedInv.salesType === 'EXPORT') {
        showToast('EXIT gate pass is required for EXPORT dispatch — create one in Gate Passes first', 'error'); return;
      }
      // LOCAL sale: gate pass optional, no block
    }
    if (!form.invoiceId && !form.invoiceNumber) {
      setNoInvoiceConfirm(true); return;
    }
    await doSave();
  };

  const del = (id) => setDelConfirmId(id);
  const doDelete = async () => {
    const id = delConfirmId; setDelConfirmId(null);
    try { await batchService.deleteTarseel(batchId, id); load(); showToast('Deleted'); }
    catch (e) { showToast(e.message, 'error'); }
  };

  const totals = rows.reduce((a,r) => ({ weight: a.weight+parseFloat(r.weightKg||0), bags: a.bags+(r.bagCount||0) }), { weight:0, bags:0 });

  const byBuyer = rows.reduce((m, r) => {
    const k = r.buyerName;
    if (!m[k]) m[k] = { name: k, containers: 0, weightKg: 0 };
    m[k].containers++;
    m[k].weightKg += parseFloat(r.weightKg || 0);
    return m;
  }, {});

  return (
    <div>
      <p style={{ color:'var(--color-text-secondary)', fontSize:13, marginTop:0 }}>
        Container/dispatch rows appear here automatically when an EXIT gate pass linked to this batch
        is dispatched via Invoice creation — every real dispatch already requires an Invoice (and, for
        Export, a Gate Pass too), so there's nothing to fill in manually here.
      </p>

      {Object.keys(byBuyer).length > 0 && (
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:16 }}>
          {Object.values(byBuyer).map(b => (
            <div key={b.name} style={{ background:'var(--color-info-light)', border:'1px solid var(--color-info-border)', borderRadius:8, padding:'10px 16px', fontSize:13 }}>
              <div style={{ fontWeight:700, color:'var(--color-primary)' }}>{b.name}</div>
              <div style={{ color:'var(--color-text-secondary)', fontSize:12 }}>{b.containers} container{b.containers!==1?'s':''} · {b.weightKg.toLocaleString()} kg</div>
            </div>
          ))}
        </div>
      )}

      {loading ? <Spinner /> : (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ background:'var(--color-neutral-50)', borderBottom:'2px solid var(--color-border-medium)' }}>
                {['Date','Container','Buyer','Weight KG','Bags','Destination','Invoice','GD','GP',''].map(h => (
                  <th key={h} style={{ padding:'8px 10px', textAlign:['Weight KG','Bags'].includes(h)?'right':'left', color:'var(--color-neutral-700)', fontWeight:600, whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom:'1px solid var(--color-bg-tertiary)', background: (!r.invoice && !r.invoiceNumber) ? 'var(--color-warning-light)' : undefined }}>
                  <td style={{ padding:'7px 10px' }}>{r.dispatchDate}</td>
                  <td style={{ padding:'7px 10px', fontFamily: 'var(--font-mono)', fontWeight:600 }}>{r.containerNumber}</td>
                  <td style={{ padding:'7px 10px' }}>
                    {r.customer ? <span style={{ color:'var(--color-primary)' }}>{r.buyerName}</span> : r.buyerName}
                  </td>
                  <td style={{ padding:'7px 10px', textAlign:'right', fontWeight:600 }}>{Number(r.weightKg).toLocaleString()}</td>
                  <td style={{ padding:'7px 10px', textAlign:'right' }}>{r.bagCount?.toLocaleString() || '—'}</td>
                  <td style={{ padding:'7px 10px', color:'var(--color-text-secondary)' }}>{r.destinationPort ? `${r.destinationPort}, ${r.destinationCountry}` : '—'}</td>
                  <td style={{ padding:'7px 10px' }}>
                    {r.invoice
                      ? <span style={{ color:'var(--color-primary)', fontSize:11, fontWeight:600 }}>{r.invoiceNumber || r.invoice?.invoiceNumber}</span>
                      : r.invoiceNumber
                        ? <span style={{ color:'var(--color-warning)', fontSize:11 }}>{r.invoiceNumber}</span>
                        : <span style={{ background:'var(--color-error-light)', color:'var(--color-error)', fontSize:10, fontWeight:600, padding:'1px 6px', borderRadius:4 }}>NO INVOICE</span>}
                  </td>
                  <td style={{ padding:'7px 10px' }}>
                    {r.goodsDeclaration
                      ? <span style={{ color:'var(--color-success)', fontSize:11, fontWeight:600 }}>{r.goodsDeclaration?.gdNumber}</span>
                      : <span style={{ color:'var(--color-text-tertiary)', fontSize:11 }}>—</span>}
                  </td>
                  <td style={{ padding:'7px 10px' }}>
                    {r.gatePass
                      ? <span style={{ color:'var(--color-accent-hover)', fontSize:11, fontWeight:600 }}>{r.gatePass?.gatePassNumber}</span>
                      : <span style={{ color:'var(--color-text-tertiary)', fontSize:11 }}>—</span>}
                  </td>
                  <td><button onClick={() => del(r.id)} style={{ background:'var(--color-error-light)', color:'var(--color-error)', border:'none', borderRadius:4, padding:'2px 8px', cursor:'pointer', fontSize:11 }}>✕</button></td>
                </tr>
              ))}
              {rows.length > 0 && (
                <tr style={{ background:'var(--color-neutral-50)', fontWeight:700, borderTop:'2px solid var(--color-border-medium)' }}>
                  <td colSpan={3} style={{ padding:'8px 10px' }}>TOTAL ({rows.length} containers)</td>
                  <td style={{ padding:'8px 10px', textAlign:'right' }}>{totals.weight.toLocaleString()} kg</td>
                  <td style={{ padding:'8px 10px', textAlign:'right' }}>{totals.bags.toLocaleString()}</td>
                  <td colSpan={5}></td>
                </tr>
              )}
            </tbody>
          </table>
          {rows.length === 0 && <p style={{ textAlign:'center', color:'var(--color-text-tertiary)', padding:20 }}>No dispatches yet.</p>}
        </div>
      )}
      {delConfirmId && <ConfirmModal message="Delete this dispatch entry?" onConfirm={doDelete} onCancel={() => setDelConfirmId(null)} />}

      {noInvoiceConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:28, maxWidth:420, width:'90%', boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }}>
            <div className="notice-mark notice-mark-warning" aria-hidden="true">!</div>
            <h3 style={{ margin:'0 0 10px', fontSize:16, color:'var(--color-warning-strong)' }}>No Invoice Linked</h3>
            <p style={{ margin:'0 0 20px', fontSize:13, color:'var(--color-text-secondary)', lineHeight:1.5 }}>
              This dispatch has no invoice. Every dispatch should have a matching sale invoice.
              You can proceed now and link the invoice after, but do not forget.
            </p>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setNoInvoiceConfirm(false)}
                style={{ padding:'7px 18px', border:'1px solid var(--color-border-strong)', borderRadius:7, background:'#fff', cursor:'pointer', fontSize:13 }}>
                Cancel
              </button>
              <button onClick={doSave} disabled={saving}
                style={{ padding:'7px 18px', border:'none', borderRadius:7, background:'var(--color-warning)', color:'#fff', cursor:'pointer', fontWeight:600, fontSize:13 }}>
                {saving ? 'Saving…' : 'Dispatch Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Linked Docs Tab ───────────────────────────────────────────────────────────

const INV_STATUS_COLOR = { DRAFT:'var(--color-bg-tertiary)', SENT:'var(--color-info-light)', PAID:'var(--color-success-light)', PARTIAL:'var(--color-warning-light)', OVERDUE:'var(--color-error-light)', CANCELLED:'var(--color-bg-tertiary)' };
const GD_STATUS_COLOR  = { FILED:'var(--color-bg-tertiary)', DOCUMENTS_SUBMITTED:'var(--color-info-light)', SETTLEMENT_PENDING:'var(--color-warning-light)', SETTLED:'var(--color-success-light)', CANCELLED:'var(--color-bg-tertiary)' };
const GP_STATUS_COLOR  = { DRAFT:'var(--color-bg-tertiary)', ISSUED:'var(--color-info-light)', RECEIVED:'var(--color-success-light)', DISPATCHED:'var(--color-info-light)', CANCELLED:'var(--color-error-light)' };

const LinkedDocsTab = ({ docs, reload, batchId }) => {
  useEffect(() => { if (!docs) reload(); }, []);

  if (!docs) return <Spinner />;

  const { gatePasses = [], invoices = [], goodsDeclarations = [] } = docs;

  const Section = ({ title, color, children }) => (
    <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden', marginBottom:16 }}>
      <div style={{ background: color, padding:'10px 16px', fontWeight:600, fontSize:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span>{title}</span>
        <span style={{ fontSize:12, fontWeight:400, color:'var(--color-text-secondary)' }}>{children.props.count} linked</span>
      </div>
      {children}
    </div>
  );

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <p style={{ margin:0, color:'var(--color-text-secondary)', fontSize:13 }}>
          Documents automatically linked when Tarseel entries reference them.
        </p>
        <button onClick={reload} style={{ background:'var(--color-bg-tertiary)', border:'none', borderRadius:6, padding:'6px 12px', cursor:'pointer', fontSize:12 }}>🔄 Refresh</button>
      </div>

      {/* Invoices */}
      <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden', marginBottom:16 }}>
        <div style={{ background:'var(--color-info-light)', padding:'10px 16px', fontWeight:600, fontSize:14, display:'flex', justifyContent:'space-between' }}>
          <span>Invoices</span>
          <span style={{ fontSize:12, color:'var(--color-text-secondary)' }}>{invoices.length} linked</span>
        </div>
        {invoices.length === 0
          ? <p style={{ padding:16, color:'var(--color-text-tertiary)', margin:0, fontSize:13 }}>No invoices linked yet. Link via Tarseel tab.</p>
          : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr style={{ borderBottom:'1px solid var(--color-border-medium)' }}>
                {['Invoice No','Customer','Date','Amount','Status'].map(h => <th key={h} style={{ padding:'8px 16px', textAlign:h==='Amount'?'right':'left', color:'var(--color-neutral-700)', fontWeight:600 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id} style={{ borderBottom:'1px solid var(--color-bg-tertiary)' }}>
                    <td style={{ padding:'8px 16px', fontWeight:600, color:'var(--color-primary)' }}>{inv.invoiceNumber}</td>
                    <td style={{ padding:'8px 16px' }}>{inv.customerName || '—'}</td>
                    <td style={{ padding:'8px 16px', color:'var(--color-text-secondary)' }}>{inv.invoiceDate}</td>
                    <td style={{ padding:'8px 16px', textAlign:'right', fontWeight:600 }}>{inv.currency} {Number(inv.totalAmount||0).toLocaleString()}</td>
                    <td style={{ padding:'8px 16px' }}>
                      <span style={{ background: INV_STATUS_COLOR[inv.status]||'var(--color-bg-tertiary)', padding:'2px 8px', borderRadius:9999, fontSize:11, fontWeight:600 }}>{inv.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
        }
      </div>

      {/* GDs */}
      <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden', marginBottom:16 }}>
        <div style={{ background:'var(--color-success-light)', padding:'10px 16px', fontWeight:600, fontSize:14, display:'flex', justifyContent:'space-between' }}>
          <span>Goods Declarations</span>
          <span style={{ fontSize:12, color:'var(--color-text-secondary)' }}>{goodsDeclarations.length} linked</span>
        </div>
        {goodsDeclarations.length === 0
          ? <p style={{ padding:16, color:'var(--color-text-tertiary)', margin:0, fontSize:13 }}>No GDs linked yet.</p>
          : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr style={{ borderBottom:'1px solid var(--color-border-medium)' }}>
                {['GD Number','Filed','Invoice USD','Net PKR','Settlement','Status'].map(h => <th key={h} style={{ padding:'8px 16px', textAlign:['Invoice USD','Net PKR'].includes(h)?'right':'left', color:'var(--color-neutral-700)', fontWeight:600 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {goodsDeclarations.map(gd => (
                  <tr key={gd.id} style={{ borderBottom:'1px solid var(--color-bg-tertiary)' }}>
                    <td style={{ padding:'8px 16px', fontWeight:600, color:'var(--color-success)' }}>{gd.gdNumber}</td>
                    <td style={{ padding:'8px 16px', color:'var(--color-text-secondary)' }}>{gd.filingDate}</td>
                    <td style={{ padding:'8px 16px', textAlign:'right' }}>{gd.invoiceValue ? usd(gd.invoiceValue) : '—'}</td>
                    <td style={{ padding:'8px 16px', textAlign:'right', fontWeight:600 }}>{gd.netPkrReceived ? pkr(gd.netPkrReceived) : '—'}</td>
                    <td style={{ padding:'8px 16px', color:'var(--color-text-secondary)' }}>{gd.settlementDate || '—'}</td>
                    <td style={{ padding:'8px 16px' }}>
                      <span style={{ background: GD_STATUS_COLOR[gd.status]||'var(--color-bg-tertiary)', padding:'2px 8px', borderRadius:9999, fontSize:11, fontWeight:600 }}>{fmt(gd.status)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
        }
      </div>

      {/* Gate Passes */}
      <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden' }}>
        <div style={{ background:'var(--color-accent-light)', padding:'10px 16px', fontWeight:600, fontSize:14, display:'flex', justifyContent:'space-between' }}>
          <span>Gate Passes</span>
          <span style={{ fontSize:12, color:'var(--color-text-secondary)' }}>{gatePasses.length} linked</span>
        </div>
        {gatePasses.length === 0
          ? <p style={{ padding:16, color:'var(--color-text-tertiary)', margin:0, fontSize:13 }}>No gate passes linked yet.</p>
          : <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr style={{ borderBottom:'1px solid var(--color-border-medium)' }}>
                {['GP Number','Type','Party','Date','Status'].map(h => <th key={h} style={{ padding:'8px 16px', textAlign:'left', color:'var(--color-neutral-700)', fontWeight:600 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {gatePasses.map(gp => (
                  <tr key={gp.id} style={{ borderBottom:'1px solid var(--color-bg-tertiary)' }}>
                    <td style={{ padding:'8px 16px', fontWeight:600, color:'var(--color-accent-hover)' }}>{gp.gatePassNumber}</td>
                    <td style={{ padding:'8px 16px' }}>{gp.type}</td>
                    <td style={{ padding:'8px 16px' }}>{gp.partyName}</td>
                    <td style={{ padding:'8px 16px', color:'var(--color-text-secondary)' }}>{gp.gatePassDate}</td>
                    <td style={{ padding:'8px 16px' }}>
                      <span style={{ background: GP_STATUS_COLOR[gp.status]||'var(--color-bg-tertiary)', padding:'2px 8px', borderRadius:9999, fontSize:11, fontWeight:600 }}>{gp.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
        }
      </div>

      {/* Batch-level documents — quality certs, lab reports, photos. Separate from the
          auto-linked records above, which come from Tarseel references, not uploads. */}
      <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, padding:16, marginTop:16 }}>
        <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>Batch Documents</div>
        <p style={{ margin:'0 0 4px', color:'var(--color-text-secondary)', fontSize:13 }}>Quality certificates, lab reports, photos — attached directly to this batch.</p>
        <AttachmentPanel entityType="BATCH" entityId={batchId} />
      </div>
    </div>
  );
};

// ── P&L Summary Tab ───────────────────────────────────────────────────────────

const PnlTab = ({ pnl, reload }) => {
  useEffect(() => { reload(); }, []);

  if (!pnl) return <Spinner />;

  const costPerKg      = Number(pnl.costPerKgExported || 0);
  const revenuePerKg   = Number(pnl.revenuePerKg || 0);
  const marginPerKg    = Number(pnl.marginPerKg || 0);
  const hasRevenue     = pnl.revenueSettled === true;
  const hasLocalSales  = Number(pnl.localInvoiceTotal || 0) > 0;

  const row = (label, value, color) => (
    <tr style={{ borderBottom:'1px solid var(--color-bg-tertiary)' }}>
      <td style={{ padding:'9px 16px', color:'var(--color-neutral-700)' }}>{label}</td>
      <td style={{ padding:'9px 16px', textAlign:'right', fontWeight:600, color: color||'var(--color-text-primary)' }}>{value}</td>
    </tr>
  );

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <h3 style={{ margin:0, fontSize:16 }}>Batch P&amp;L Summary</h3>
        <button onClick={reload} style={{ background:'var(--color-bg-tertiary)', border:'none', borderRadius:6, padding:'6px 12px', cursor:'pointer', fontSize:12 }}>🔄 Refresh</button>
      </div>

      {/* Top stat cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:12, marginBottom:20 }}>
        <StatCard label="Purchased (Amad)" value={kg(pnl.amadWeightKg)} sub={pkr(pnl.amadTotal)} color="var(--color-primary)" />
        <StatCard label="Sorted Out" value={`${pnl.deductBags?.toLocaleString()||0} bags`} sub={`Recovery: ${pkr(pnl.deductRecovery)}`} color="var(--color-warning)" />
        <StatCard label="Exported" value={kg(pnl.exportWeightKg)} sub={`${pnl.exportBags?.toLocaleString()||0} bags`} color="var(--color-success)" />
        <StatCard label="All-In Cost / KG" value={`PKR ${costPerKg.toFixed(2)}`} sub="sourcing + labor + bardana" color="var(--color-accent-hover)" />
        {hasRevenue && <>
          {/* Total Revenue below is Invoice-driven only (any sent invoice, local or export) —
              decoupled from GD settlement 2026-09-14. GD Settlement is its own separate
              customs/export-paperwork figure, shown for reference only; it does not feed
              Total Revenue and may lag behind it (GD gets settled later, after the sale). */}
          {Number(pnl.settledRevenuePkr || 0) > 0 &&
            <StatCard label="GD Settlement (Export Docs)" value={pkr(pnl.settledRevenuePkr)} sub={`Invoice: ${usd(pnl.invoiceValueUsd)}`} color="var(--color-info)" />}
          {hasLocalSales &&
            <StatCard label="Local Sales Invoiced" value={pkr(pnl.localInvoiceTotal)} sub={`Paid so far: ${pkr(pnl.localRevenuePkr)}`} color="var(--color-accent-hover)" />}
          <StatCard label="Total Revenue" value={pkr(pnl.totalRevenuePkr)} sub={`${pkr(marginPerKg)} / kg margin`} color="var(--color-success)" />
          <StatCard label="Gross Profit" value={pkr(pnl.profitPkr)} sub={`Cost: ${pkr(pnl.totalCost)}`}
            color={Number(pnl.profitPkr) >= 0 ? 'var(--color-success)' : 'var(--color-error)'} />
        </>}
        {!hasRevenue && (
          <div style={{ background:'var(--color-warning-light)', border:'1px solid var(--color-warning-border)', borderRadius:10, padding:'14px 18px' }}>
            <div style={{ fontSize:12, color:'var(--color-warning-strong)', marginBottom:4 }}>Revenue</div>
            <div style={{ fontSize:13, color:'var(--color-warning)' }}>No invoice sent yet</div>
            <div style={{ fontSize:11, color:'var(--color-text-tertiary)', marginTop:4 }}>
              Revenue shows once a Sales Invoice linked to this batch (local or export) is sent —
              independent of GD, which is a separate customs/export document.
            </div>
          </div>
        )}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:16 }}>
        {/* Cost breakdown */}
        <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden' }}>
          <div style={{ background:'var(--color-neutral-50)', padding:'10px 16px', borderBottom:'1px solid var(--color-border-medium)', fontWeight:600, fontSize:14 }}>Cost Breakdown</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <tbody>
              {row('Sourcing (Amad)', pkr(pnl.amadTotal))}
              {row('Labor (loading + ops)', pkr(pnl.laborTotal))}
              {row('Bardana (packaging)', pkr(pnl.bardanaTotal))}
              {Number(pnl.shipmentCostTotal || 0) > 0 && row('Shipment Costs (freight + clearing)', pkr(pnl.shipmentCostTotal))}
              {Number(pnl.wastageLossPkr || 0) > 0 && row('Wastage / Loss', `+ ${pkr(pnl.wastageLossPkr)}`, 'var(--color-error)')}
              {row('Less: Deduction Recovery', `– ${pkr(pnl.deductRecovery)}`, 'var(--color-warning)')}
              <tr style={{ background:'var(--color-success-light)', borderTop:'2px solid var(--color-border-medium)' }}>
                <td style={{ padding:'10px 16px', fontWeight:700 }}>Net Total Cost</td>
                <td style={{ padding:'10px 16px', textAlign:'right', fontWeight:700, color:'var(--color-success)', fontSize:15 }}>{pkr(pnl.totalCost)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Revenue / margin (shown once any invoice linked to this batch has been sent — no longer tied to GD settlement) */}
        <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden' }}>
          <div style={{ background:'var(--color-neutral-50)', padding:'10px 16px', borderBottom:'1px solid var(--color-border-medium)', fontWeight:600, fontSize:14 }}>
            {hasRevenue ? 'Revenue & Margin' : 'Quantity Reconciliation'}
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <tbody>
              {hasRevenue ? <>
                {Number(pnl.settledRevenuePkr || 0) > 0 && row('GD Settlement — Export Docs (PKR, separate from Total Revenue)', pkr(pnl.settledRevenuePkr), 'var(--color-info)')}
                {Number(pnl.invoiceValueUsd || 0) > 0 && row('Export Invoice Value (USD)', usd(pnl.invoiceValueUsd))}
                {hasLocalSales && row('Local Sales — Invoiced (PKR)', pkr(pnl.localInvoiceTotal), 'var(--color-accent-hover)')}
                {hasLocalSales && row('Local Sales — Received (PKR)', pkr(pnl.localRevenuePkr), 'var(--color-accent-hover)')}
                {row('Total Revenue (PKR)', pkr(pnl.totalRevenuePkr), 'var(--color-success)')}
                {row('Total Cost (PKR)', pkr(pnl.totalCost), 'var(--color-warning)')}
                <tr style={{ background: Number(pnl.profitPkr) >= 0 ? 'var(--color-success-light)' : 'var(--color-error-light)', borderTop:'2px solid var(--color-border-medium)' }}>
                  <td style={{ padding:'10px 16px', fontWeight:700 }}>Gross Profit</td>
                  <td style={{ padding:'10px 16px', textAlign:'right', fontWeight:700, color: Number(pnl.profitPkr) >= 0 ? 'var(--color-success)' : 'var(--color-error)', fontSize:15 }}>{pkr(pnl.profitPkr)}</td>
                </tr>
                {row('Revenue / KG', `PKR ${revenuePerKg.toFixed(2)}`, 'var(--color-info)')}
                {row('Cost / KG', `PKR ${costPerKg.toFixed(2)}`)}
                {row('Margin / KG', `PKR ${marginPerKg.toFixed(2)}`, Number(pnl.marginPerKg) >= 0 ? 'var(--color-success)' : 'var(--color-error)')}
              </> : <>
                {row('Amad bags purchased', (pnl.amadBags||0).toLocaleString())}
                {row('Less: sorted out', `– ${(pnl.deductBags||0).toLocaleString()} bags`, 'var(--color-warning)')}
                {row('Net available', (pnl.netBags||0).toLocaleString())}
                {row('Exported (Tarseel)', (pnl.exportBags||0).toLocaleString(), 'var(--color-success)')}
                {(pnl.deductBags||0) === 0 && (pnl.exportBags||0) === 0
                  ? row('Shrinkage / gap', 'Not yet processed', 'var(--color-text-tertiary)')
                  : row('Shrinkage / gap', `${Number(pnl.shrinkageKg||0).toLocaleString()} kg`, 'var(--color-text-secondary)')}
              </>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Grading breakdown */}
      {pnl.gradeSummary?.length > 0 && (
        <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden', marginBottom:16 }}>
          <div style={{ background:'var(--color-neutral-50)', padding:'10px 16px', borderBottom:'1px solid var(--color-border-medium)', fontWeight:600, fontSize:14 }}>Grading Deductions by Type</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead><tr style={{ borderBottom:'1px solid var(--color-border-medium)' }}>
              {['Grade','Bags','Weight KG','Recovery (PKR)','Loss (PKR)'].map(h => <th key={h} style={{ padding:'8px 16px', textAlign:h==='Grade'?'left':'right', color:'var(--color-neutral-700)', fontWeight:600 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {pnl.gradeSummary.map(g => (
                <tr key={g.grade} style={{ borderBottom:'1px solid var(--color-bg-tertiary)' }}>
                  <td style={{ padding:'8px 16px', fontWeight:600 }}>{g.grade}</td>
                  <td style={{ padding:'8px 16px', textAlign:'right' }}>{Number(g.bags).toLocaleString()}</td>
                  <td style={{ padding:'8px 16px', textAlign:'right' }}>{Number(g.weightKg).toLocaleString()}</td>
                  <td style={{ padding:'8px 16px', textAlign:'right' }}>{Number(g.recovery) > 0 ? pkr(g.recovery) : '—'}</td>
                  <td style={{ padding:'8px 16px', textAlign:'right', color: Number(g.loss) > 0 ? 'var(--color-error)' : undefined }}>{Number(g.loss) > 0 ? pkr(g.loss) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Buyer summary */}
      {pnl.buyerSummary?.length > 0 && (
        <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, overflow:'hidden' }}>
          <div style={{ background:'var(--color-neutral-50)', padding:'10px 16px', borderBottom:'1px solid var(--color-border-medium)', fontWeight:600, fontSize:14 }}>Exported by Buyer</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead><tr style={{ borderBottom:'1px solid var(--color-border-medium)' }}>
              {['Buyer','Weight KG','Bags','Cost @ Rate','Revenue / Buyer'].map(h => <th key={h} style={{ padding:'8px 16px', textAlign:['Weight KG','Bags','Cost @ Rate','Revenue / Buyer'].includes(h)?'right':'left', color:'var(--color-neutral-700)', fontWeight:600 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {pnl.buyerSummary.map(b => {
                const buyerCost = Number(b.weightKg) * costPerKg;
                const buyerRev  = hasRevenue ? Number(b.weightKg) * revenuePerKg : null;
                return (
                  <tr key={b.buyerName} style={{ borderBottom:'1px solid var(--color-bg-tertiary)' }}>
                    <td style={{ padding:'8px 16px', fontWeight:600 }}>{b.buyerName}</td>
                    <td style={{ padding:'8px 16px', textAlign:'right' }}>{Number(b.weightKg).toLocaleString()}</td>
                    <td style={{ padding:'8px 16px', textAlign:'right' }}>{Number(b.bags).toLocaleString()}</td>
                    <td style={{ padding:'8px 16px', textAlign:'right', color:'var(--color-accent-hover)' }}>{pkr(buyerCost)}</td>
                    <td style={{ padding:'8px 16px', textAlign:'right', color: buyerRev ? 'var(--color-info)' : 'var(--color-text-tertiary)' }}>
                      {buyerRev ? pkr(buyerRev) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, sub, color }) => (
  <div style={{ background:'#fff', border:'1px solid var(--color-border-medium)', borderRadius:10, padding:'14px 18px' }}>
    <div style={{ fontSize:12, color:'var(--color-text-secondary)', marginBottom:4 }}>{label}</div>
    <div style={{ fontSize:20, fontWeight:700, color: color||'var(--color-text-primary)' }}>{value}</div>
    {sub && <div style={{ fontSize:12, color:'var(--color-text-secondary)', marginTop:2 }}>{sub}</div>}
  </div>
);

export default BatchManagement;
